import supabase from './supabase';

class ReviewService {
  // ENHANCED METHODS FOR SCALABILITY

  // Get reviews with pagination and optimized queries
  async getReviewsPaginated(filters = {}, pagination = {}) {
    try {
      const { 
        page = 1, 
        pageSize = 25, 
        cursor = null,
        loadDetails = false 
      } = pagination;

      // Calculate offset for traditional pagination
      const offset = (page - 1) * pageSize;

      // Base query with minimal fields for list view
      let selectFields = `
        id,
        rating,
        comment,
        reviewer_name,
        reviewer_email,
        status,
        submitted_at,
        created_at,
        updated_at,
        is_published,
        is_archived,
        archived_at,
        contact_id
      `;

      // Add detailed fields if requested
      if (loadDetails) {
        selectFields += `,
          contact:contacts(
            id,
            name,
            email,
            phone,
            organization_name,
            address,
            city,
            state,
            postal_code,
            service_name
          )
        `;
      }

      let query = supabase
        .from('reviews')
        .select(selectFields, { count: 'exact' })
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (typeof filters.isPublished === 'boolean') {
        query = query.eq('is_published', filters.isPublished);
      }
      if (filters.rating && filters.rating !== 'all') {
        query = query.eq('rating', filters.rating);
      }
      if (filters.dateFrom) {
        query = query.gte('submitted_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('submitted_at', filters.dateTo);
      }
      if (filters.searchTerm) {
        query = query.or(`reviewer_name.ilike.%${filters.searchTerm}%,comment.ilike.%${filters.searchTerm}%`);
      }

      // Apply pagination
      if (cursor) {
        // Cursor-based pagination for better performance with large datasets
        query = query.lt('created_at', cursor);
      } else {
        // Traditional pagination
        query = query.range(offset, offset + pageSize - 1);
      }

      query = query.limit(pageSize);

      const { data, error, count } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      // Calculate pagination metadata
      const totalPages = Math.ceil(count / pageSize);
      const hasMore = cursor ? data.length === pageSize : page < totalPages;
      const nextCursor = data.length > 0 ? data[data.length - 1].created_at : null;

      return {
        success: true,
        data,
        pagination: {
          currentPage: page,
          pageSize,
          totalItems: count,
          totalPages,
          hasMore,
          nextCursor: hasMore ? nextCursor : null
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to fetch reviews' };
    }
  }

  // Get review details by ID with full relationship data
  async getReviewDetails(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          contact:contacts(
            id,
            name,
            email,
            phone,
            organization_name,
            customer_type,
            lead_source,
            industry,
            pipeline_stage,
            tags,
            internal_notes,
            created_at,
            address,
            city,
            state,
            postal_code,
            service_name,
            lead_source_id,
            primary_service_id,
            lead_sources(name),
            primary_services(name)
          )
        `)
        .eq('id', reviewId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch review details' };
    }
  }

  // Get review statistics with caching support
  async getReviewStatisticsCached(dateRange = {}, organizationId = null, useCache = true) {
    const cacheKey = `review_stats_${JSON.stringify(dateRange)}_${organizationId}`;
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes default

    // Check cache first
    if (useCache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return { success: true, data: cached, fromCache: true };
      }
    }

    try {
      const { data, error } = await supabase
        .rpc('get_review_statistics', {
          date_from: dateRange.from || null,
          date_to: dateRange.to || null,
          org_id: organizationId
        });

      if (error) {
        // Fallback to manual calculation if RPC fails
        return await this.calculateReviewStatsManually(dateRange, organizationId);
      }

      // Cache the result
      if (useCache) {
        this.setCache(cacheKey, data, cacheExpiry);
      }

      return { success: true, data };
    } catch (error) {
      return await this.calculateReviewStatsManually(dateRange, organizationId);
    }
  }

  // Search reviews with optimized query
  async searchReviews(searchTerm, filters = {}, pagination = {}) {
    try {
      const { pageSize = 25, cursor = null } = pagination;

      let query = supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          reviewer_name,
          reviewer_email,
          status,
          submitted_at,
          created_at,
          is_published,
          contact:contacts(
            id,
            name,
            organization_name
          )
        `, { count: 'exact' })
        .eq('is_archived', false)
        .or(`reviewer_name.ilike.%${searchTerm}%,comment.ilike.%${searchTerm}%,reviewer_email.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      // Apply additional filters
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.rating && filters.rating !== 'all') {
        query = query.eq('rating', filters.rating);
      }

      // Apply cursor-based pagination
      if (cursor) {
        query = query.lt('created_at', cursor);
      }

      query = query.limit(pageSize);

      const { data, error, count } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      const hasMore = data.length === pageSize;
      const nextCursor = data.length > 0 ? data[data.length - 1].created_at : null;

      return {
        success: true,
        data,
        pagination: {
          totalItems: count,
          hasMore,
          nextCursor: hasMore ? nextCursor : null,
          pageSize
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to search reviews' };
    }
  }

  // Get archived reviews with pagination
  async getArchivedReviewsPaginated(filters = {}, pagination = {}) {
    try {
      const { page = 1, pageSize = 25 } = pagination;
      const offset = (page - 1) * pageSize;

      let query = supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          reviewer_name,
          status,
          submitted_at,
          archived_at,
          archived_reason,
          is_published,
          organization_id,
          contact:contacts(
            id,
            name,
            organization_name
          )
        `, { count: 'exact' })
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });

      // Apply filters
      if (filters.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }
      if (filters.dateFrom) {
        query = query.gte('archived_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('archived_at', filters.dateTo);
      }
      if (filters.rating && filters.rating !== 'all') {
        query = query.eq('rating', filters.rating);
      }

      query = query.range(offset, offset + pageSize - 1);

      const { data, error, count } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data,
        pagination: {
          currentPage: page,
          pageSize,
          totalItems: count,
          totalPages: Math.ceil(count / pageSize),
          hasMore: page < Math.ceil(count / pageSize)
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to fetch archived reviews' };
    }
  }

  // Cache management methods
  setCache(key, data, expiryMs = 300000) { // 5 minutes default
    const item = {
      data,
      expiry: Date.now() + expiryMs
    };
    try {
      localStorage.setItem(`reviewService_${key}`, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to set cache:', error);
    }
  }

  getFromCache(key) {
    try {
      const item = localStorage.getItem(`reviewService_${key}`);
      if (!item) return null;

      const parsed = JSON.parse(item);
      if (Date.now() > parsed.expiry) {
        localStorage.removeItem(`reviewService_${key}`);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.warn('Failed to get from cache:', error);
      return null;
    }
  }

  clearCache(pattern = '') {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('reviewService_') && key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  // EXISTING METHODS (keeping for backward compatibility)

  // Get all reviews with filters (excludes archived by default)
  async getReviews(filters = {}) {
    // Use the new paginated method but return all results for backward compatibility
    try {
      // For dashboard usage, include archived reviews by default
      const includeArchived = filters.includeArchived !== false;
      
      let result;
      if (includeArchived) {
        // Direct query without archived filter for dashboard
      let query = supabase
          .from('reviews')
          .select(`
            id,
            rating,
            comment,
            reviewer_name,
            reviewer_email,
            status,
            submitted_at,
            created_at,
            updated_at,
            is_published,
            is_archived,
            archived_at,
            contact_id,
            organization_id
          `)
          .order('created_at', { ascending: false })
          .limit(1000);

        // Apply filters
      if (filters.organizationId) {
        query = query.eq('organization_id', filters.organizationId);
      }
        if (filters.organizationId) {
          query = query.eq('organization_id', filters.organizationId);
        }
        if (filters.status && filters.status !== 'all') {
          query = query.eq('status', filters.status);
        }
        if (filters.rating && filters.rating !== 'all') {
          query = query.eq('rating', filters.rating);
        }
        if (filters.dateFrom) {
          query = query.gte('submitted_at', filters.dateFrom);
        }
        if (filters.dateTo) {
          query = query.lte('submitted_at', filters.dateTo);
        }
        if (filters.searchTerm) {
          query = query.or(`reviewer_name.ilike.%${filters.searchTerm}%,comment.ilike.%${filters.searchTerm}%`);
        }

        const { data, error } = await query;
        
        if (error) {
          return { success: false, error: error.message };
        }
        
        result = { success: true, data };
      } else {
        // Use paginated method for non-dashboard usage
        result = await this.getReviewsPaginated(filters, { 
          page: 1, 
          pageSize: 1000, 
          loadDetails: true 
        });
        
        if (result.success) {
          result = { success: true, data: result.data };
        }
      }
      
      return result;
    } catch (error) {
      return { success: false, error: 'Failed to fetch reviews' };
    }
  }

  // Get review by ID
  async getReviewById(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          contact:contacts(*)
        `)
        .eq('id', reviewId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch review details' };
    }
  }

  // Create new review (public endpoint for customers)
  async createReview(reviewData) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert([{
          ...reviewData,
          status: 'pending',
          submitted_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to create review' };
    }
  }

  // Update review
  async updateReview(reviewId, updates) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to update review' };
    }
  }

  // Delete review
  async deleteReview(reviewId) {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete review' };
    }
  }

  // Get reviews for a specific appointment
  async getReviewsByContact(contactId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch contact reviews' };
    }
  }

  // Get review statistics
  async getReviewStatistics(dateRange = {}, organizationId = null) {
    try {
      const { data, error } = await supabase
        .rpc('get_review_statistics', {
          date_from: dateRange.from || null,
          date_to: dateRange.to || null,
          org_id: organizationId
        });

      if (error) {
        // Fallback to manual calculation if RPC fails
        return await this.calculateReviewStatsManually(dateRange, organizationId);
      }

      return { success: true, data };
    } catch (error) {
      return await this.calculateReviewStatsManually(dateRange, organizationId);
    }
  }

  // Manual calculation of review stats (fallback)
  async calculateReviewStatsManually(dateRange = {}, organizationId = null) {
    try {
      let query = supabase
        .from('reviews')
        .select('rating, status, submitted_at, created_at, organization_id');

      if (dateRange.from) {
        query = query.gte('submitted_at', dateRange.from);
      }
      if (dateRange.to) {
        query = query.lte('submitted_at', dateRange.to);
      }
      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      // Calculate statistics
      const stats = {
        total_reviews: data.length,
        submitted_reviews: data.filter(r => r.status === 'submitted' || r.status === 'archived').length,
        pending_reviews: data.filter(r => r.status === 'pending').length,
        positive_reviews: data.filter(r => r.rating >= 4).length,
        negative_reviews: data.filter(r => r.rating <= 2).length,
        average_rating: data.length > 0 ? 
          Math.round((data.reduce((sum, r) => sum + (r.rating || 0), 0) / data.length) * 100) / 100 : 0,
        rating_distribution: this.calculateRatingDistribution(data),
        response_rate: data.length > 0 ? 
          Math.round((data.filter(r => r.status === 'submitted' || r.status === 'archived').length / data.length) * 100) : 0
      };

      return { success: true, data: stats };
    } catch (error) {
      return { success: false, error: 'Failed to calculate review statistics' };
    }
  }

  // Calculate rating distribution
  calculateRatingDistribution(reviews) {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach(review => {
      if (review.rating && review.rating >= 1 && review.rating <= 5) {
        distribution[review.rating]++;
      }
    });
    return distribution;
  }

  // Review Templates Methods

  // Get all review templates
  async getReviewTemplates(filters = {}) {
    try {
      let query = supabase
        .from('review_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch review templates' };
    }
  }

  // Create review template
  async createReviewTemplate(templateData) {
    try {
      const { data, error } = await supabase
        .from('review_templates')
        .insert([{
          ...templateData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to create review template' };
    }
  }

  // Update review template
  async updateReviewTemplate(templateId, updates) {
    try {
      const { data, error } = await supabase
        .from('review_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to update review template' };
    }
  }

  // Delete review template
  async deleteReviewTemplate(templateId) {
    try {
      const { error } = await supabase
        .from('review_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete review template' };
    }
  }

  // Review Requests Methods

  // Get review requests
  async getReviewRequests(filters = {}) {
    try {
      let query = supabase
        .from('review_requests')
        .select(`
          *,
          review:reviews(*),
          template:review_templates(*)
        `)
        .order('sent_at', { ascending: false });

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.method) {
        query = query.eq('method', filters.method);
      }
      if (filters.dateFrom) {
        query = query.gte('sent_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('sent_at', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch review requests' };
    }
  }

  // Create review request
  async createReviewRequest(requestData) {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .insert([{
          ...requestData,
          sent_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to create review request' };
    }
  }

  // Update review request status by customer
  async updateReviewRequestStatusByCustomer(customerId, status) {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .update({
          status,
          responded_at: status === 'responded' ? new Date().toISOString() : null
        })
        .eq('customer_id', customerId)
        .select();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to update review request status' };
    }
  }

  // Send review reminder
  async sendReviewReminder(requestId) {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .update({
          reminder_sent_at: new Date().toISOString(),
          reminder_count: supabase.sql`reminder_count + 1`
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to send review reminder' };
    }
  }

  // Review Settings Methods

  // Get review settings
  async getReviewSettings() {
    try {
      const { data, error } = await supabase
        .from('review_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch review settings' };
    }
  }

  // Update review settings
  async updateReviewSettings(settingsData) {
    try {
      // First get the existing settings ID
      const existingResult = await this.getReviewSettings();
      
      if (existingResult.success && existingResult.data) {
        // Update existing settings
        const { data, error } = await supabase
          .from('review_settings')
          .update({
            ...settingsData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingResult.data.id)
          .select()
          .single();

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, data };
      } else {
        // Create new settings
        const { data, error } = await supabase
          .from('review_settings')
          .insert([{
            ...settingsData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, data };
      }
    } catch (error) {
      return { success: false, error: 'Failed to update review settings' };
    }
  }

  // Get recent reviews
  async getRecentReviews(limit = 10, organizationId = null) {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          contact:contacts(
            id,
            name,
            email,
            phone,
            service_name,
            organization_id
          )
        `)
        .eq('status', 'submitted')
        .order('submitted_at', { ascending: false })
        .limit(limit);

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch recent reviews' };
    }
  }

  // Get pending review requests (for sending reminders)
  async getPendingReviewRequests() {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .select(`
          *
        `)
        .eq('status', 'pending')
        .lt('sent_at', new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()) // 3 days ago
        .lt('reminder_count', 2); // Max 2 reminders

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch pending review requests' };
    }
  }

  // Validate review token and get customer/request info
  async validateReviewToken(token) {
    try {
      const { data, error } = await supabase
        .rpc('get_review_data_by_token', { p_token: token });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data || data.length === 0) {
        return { success: false, error: 'Invalid token' };
      }

      const reviewData = data[0];
      
      if (!reviewData.is_valid) {
        return { success: false, error: reviewData.error_message || 'Invalid token' };
      }

      // Build customer object from the returned data
      const customer = {
        id: reviewData.customer_id,
        email: reviewData.customer_email,
        name: reviewData.customer_name,
        phone: reviewData.customer_phone,
        organization_id: reviewData.organization_id,
        primary_service: reviewData.service_name ? {
          name: reviewData.service_name
        } : null
      };

      // Build organization object from the returned data
      const organization = {
        id: reviewData.organization_id,
        name: reviewData.organization_name,
        business_name: reviewData.organization_business_name,
        logo_url: reviewData.organization_logo_url,
        business_type: reviewData.organization_business_type
      };

      // Build review request object
      const reviewRequest = {
        id: reviewData.review_request_id,
        campaign_config: reviewData.campaign_config
      };

      return {
        success: true,
        data: {
          reviewRequestId: reviewData.review_request_id,
          customerId: reviewData.customer_id,
          customerEmail: reviewData.customer_email,
          customer: customer,
          organization: organization,
          reviewRequest: reviewRequest
        }
      };
    } catch (error) {
      console.error('Error validating review token:', error);
      return { success: false, error: 'Failed to validate token' };
    }
  }

  // Submit review using token
  async submitReviewWithToken(token, reviewData) {
    try {
      // First validate the token
      const tokenValidation = await this.validateReviewToken(token);
      if (!tokenValidation.success) {
        return tokenValidation;
      }

      const { reviewRequestId, customerId, customer } = tokenValidation.data;

      // We have the review request ID and customer info from token validation

      // Create the review
      const reviewInsertData = {
        rating: reviewData.rating,
        comment: reviewData.comment || '',
        reviewer_name: reviewData.reviewer_name || customer.name,
        reviewer_email: reviewData.reviewer_email || customer.email,
        reviewer_phone: reviewData.reviewer_phone || customer.phone,
        status: 'pending',
        submitted_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        contact_id: customer.id,
        organization_id: customer.organization_id
      };

      const { data: reviewRecord, error: reviewError } = await supabase
        .from('reviews')
        .insert([reviewInsertData])
        .select()
        .single();

      if (reviewError) {
        console.error('Error creating review:', reviewError);
        return { success: false, error: reviewError.message };
      }

      // NEW: Update the original review_request to mark it as completed
      const { error: updateRequestError } = await supabase
        .from('review_requests')
        .update({
          status: 'completed',
          review_id: reviewRecord.id,
          responded_at: new Date().toISOString(),
          review_rating: reviewData.rating
        })
        .eq('id', reviewRequestId);

      if (updateRequestError) {
        console.error('Error updating review request status:', updateRequestError);
        // Don't fail the whole operation, but log the error
      }

      // Mark review as completed using the database function
      const { error: markError } = await supabase
        .rpc('mark_review_completed', {
          p_token: token,
          p_rating: reviewData.rating,
          p_review_id: reviewRecord.id
        });

      if (markError) {
        console.error('Error marking review completed:', markError);
        // Continue anyway since the review was created
      }

      return { success: true, data: reviewRecord };
    } catch (error) {
      console.error('Error submitting review with token:', error);
      return { success: false, error: 'Failed to submit review' };
    }
  }

  // Get pending review requests that need reminders
  async getPendingReminders() {
    try {
      const { data, error } = await supabase
        .rpc('get_pending_reminder_requests');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error getting pending reminders:', error);
      return { success: false, error: 'Failed to get pending reminders' };
    }
  }

  // Mark reminder as sent
  async markReminderSent(requestId) {
    try {
      const { error } = await supabase
        .rpc('mark_reminder_sent', { p_request_id: requestId });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error marking reminder sent:', error);
      return { success: false, error: 'Failed to mark reminder sent' };
    }
  }

  // Create review token for a review request
  async createReviewToken(reviewRequestId, customerId, customerEmail, expiresHours = 168) {
    try {
      const { data, error } = await supabase
        .rpc('create_review_token', {
          p_review_request_id: reviewRequestId,
          p_customer_id: customerId,
          p_customer_email: customerEmail,
          p_expires_hours: expiresHours
        });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: { token: data } };
    } catch (error) {
      console.error('Error creating review token:', error);
      return { success: false, error: 'Failed to create review token' };
    }
  }

  // Get review request by ID with token
  async getReviewRequestWithToken(requestId) {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .select(`
          *,
          customer:contacts(*),
          review_tokens(*)
        `)
        .eq('id', requestId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error getting review request with token:', error);
      return { success: false, error: 'Failed to get review request' };
    }
  }

  // Update reminder due time based on settings
  async updateReminderDueTime(requestId, reminderDelayHours = 72) {
    try {
      const { error } = await supabase
        .from('review_requests')
        .update({
          reminder_due_at: new Date(Date.now() + reminderDelayHours * 60 * 60 * 1000).toISOString()
        })
        .eq('id', requestId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating reminder due time:', error);
      return { success: false, error: 'Failed to update reminder time' };
    }
  }

  // Archive old completed reviews (automated process)
  async archiveOldReviews() {
    try {
      const { data, error } = await supabase
        .rpc('archive_old_reviews');

      if (error) {
        console.error('Error archiving old reviews:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        archivedCount: data,
        message: `Successfully archived ${data} old reviews`
      };
    } catch (error) {
      console.error('Error in archiveOldReviews:', error);
      return { success: false, error: 'Failed to archive old reviews' };
    }
  }

  // Manually archive a specific review
  async archiveReview(reviewId, reason = 'Manually archived') {
    try {
      // Use direct update instead of RPC for better reliability
      const { data, error } = await supabase
        .from('reviews')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          status: 'archived',
          archived_reason: reason
        })
        .eq('id', reviewId)
        .select();

      if (error) {
        console.error('Error archiving review:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        data: data,
        message: 'Review archived successfully'
      };
    } catch (error) {
      console.error('Error in archiveReview:', error);
      return { success: false, error: 'Failed to archive review' };
    }
  }

  // Restore an archived review
  async restoreReview(reviewId) {
    try {
      // Use direct update instead of RPC for better reliability
      const { data, error } = await supabase
        .from('reviews')
        .update({
          is_archived: false,
          archived_at: null,
          status: 'submitted',
          archived_reason: null
        })
        .eq('id', reviewId)
        .select();

      if (error) {
        console.error('Error restoring review:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        data: data,
        message: 'Review restored successfully'
      };
    } catch (error) {
      console.error('Error in restoreReview:', error);
      return { success: false, error: 'Failed to restore review' };
    }
  }

  // Approve review (change status from pending to submitted)
  async approveReview(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({
          status: 'submitted',
          is_published: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select();

      if (error) {
        console.error('Error approving review:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        data: data,
        message: 'Review approved successfully'
      };
    } catch (error) {
      console.error('Error in approveReview:', error);
      return { success: false, error: 'Failed to approve review' };
    }
  }

  // Publish / Unpublish / Reject helpers
  async publishReview(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({ is_published: true, status: 'submitted', updated_at: new Date().toISOString() })
        .eq('id', reviewId)
        .select();
      if (error) return { success: false, error: error.message };
      return { success: true, data, message: 'Review published' };
    } catch (error) {
      return { success: false, error: 'Failed to publish review' };
    }
  }

  async unpublishReview(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({ is_published: false, updated_at: new Date().toISOString() })
        .eq('id', reviewId)
        .select();
      if (error) return { success: false, error: error.message };
      return { success: true, data, message: 'Review unpublished' };
    } catch (error) {
      return { success: false, error: 'Failed to unpublish review' };
    }
  }

  async rejectReview(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({ status: 'rejected', is_published: false, updated_at: new Date().toISOString() })
        .eq('id', reviewId)
        .select();
      if (error) return { success: false, error: error.message };
      return { success: true, data, message: 'Review rejected' };
    } catch (error) {
      return { success: false, error: 'Failed to reject review' };
    }
  }

  // Get archived reviews with filters
  async getArchivedReviews(filters = {}) {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          contact:contacts(*)
        `)
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });

      // Apply filters
      if (filters.dateFrom) {
        query = query.gte('archived_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('archived_at', filters.dateTo);
      }
      if (filters.rating) {
        query = query.eq('rating', filters.rating);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch archived reviews' };
    }
  }

  // Get reviews excluding archived ones (updated default method)
  async getActiveReviews(filters = {}) {
    try {
      let query = supabase
        .from('reviews')
        .select(`
          *,
          contact:contacts(*)
        `)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.rating) {
        query = query.eq('rating', filters.rating);
      }
      if (filters.dateFrom) {
        query = query.gte('submitted_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('submitted_at', filters.dateTo);
      }
      // Removed technicianId filter since we no longer have appointment relationship

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to fetch active reviews' };
    }
  }

  // Process automatic archiving (can be called by cron job or scheduler)
  async processAutomaticArchiving() {
    try {
      const result = await this.archiveOldReviews();
      
      if (result.success && result.archivedCount > 0) {
        console.log(`✅ Automatic archiving: ${result.archivedCount} reviews archived`);
        
        // Return notification data for the UI
        return {
          ...result,
          shouldNotify: true,
          notificationMessage: `${result.archivedCount} reviews automatically archived`,
          notificationType: 'info'
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error in processAutomaticArchiving:', error);
      return { success: false, error: 'Failed to process automatic archiving' };
    }
  }

  // Get the full review/feedback history for a single contact
  async getReviewHistoryByContact(contactId) {
    try {
      const { data, error } = await supabase
        .from('review_requests')
        .select(`
          id,
          sent_at,
          status,
          trigger_type,
          review:reviews (
            id,
            rating,
            comment,
            submitted_at
          )
        `)
        .eq('customer_id', contactId)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching review history:', error);
        throw error;
      }

      // Supabase might return an array for the one-to-one 'review' relationship. Let's flatten it.
      const formattedData = data.map(request => ({
        ...request,
        review: Array.isArray(request.review) ? request.review[0] || null : request.review,
      }));

      return { success: true, data: formattedData };
    } catch (error) {
      console.error('Error in getReviewHistoryByContact:', error);
      return { success: false, error: 'Failed to fetch feedback history.' };
    }
  }
}

export default new ReviewService();